import { useContext, useEffect, useState, useRef } from "react";
import styles from "./CommonFields.module.css";
import {
    BlockStack,
    Button,
    ButtonGroup,
    Card,
    Checkbox,
    ChoiceList,
    Collapsible,
    Divider,
    FormLayout,
    InlineError,
    Icon,
    Label,
    RadioButton,
    Select,
    Text,
    TextField,
    Tooltip,
} from "@shopify/polaris";
import { MdOutlineArrowBackIosNew, MdOutlineTrendingUp, MdOutlineBlock } from "react-icons/md";
import { Field } from "@shopify/react-form";
import {
    BundleTypes,
    DISCOUNT_TYPES,
    DiscountTypes,
    getDiscountOptions,
    getDiscountOptionsAddons,
    getDiscountOptionsAddonsPopup,
    getDiscountOptionsBB,
    getDiscountOptionsBXGY,
    getDiscountOptionsThankyou,
    getPriceLabel,
} from "../constants";
import { AppConfigContext } from "../../../context/AppConfigContext";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import { InfoIcon, PackageIcon, DiscountIcon } from "@shopify/polaris-icons";
import { BBTierOptions, MixMatchOption } from "../Types";
import BBTierDiscounts from "./BBTierDiscounts";
import { getNumberFieldSuffix } from "../../../utilities/constants";
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import { PiBracketsCurlyBold } from "react-icons/pi";
import FixedBoxesConfig from "./FixedBoxesConfig";
import { FixedBoxConfig } from "../Types";
import MantleDoc, { mantleDocHandles } from "../../../components/MantleDoc";
import PlaceholderDiscount from "../Placeholder/PlaceholderDiscount";

export const PricingStrategyTile = ({
    title,
    description,
    icon,
    selected,
    onClick,
}: {
    title: string;
    description: string;
    icon: any;
    selected: boolean;
    onClick: () => void;
}) => (
    <div
        onClick={onClick}
        className={styles.pricingTile}
        style={{
            border: selected ? "2px solid #008060" : "1px solid #E1E3E5",
            backgroundColor: selected ? "#F0FDF4" : "white",
        }}
    >
        {selected && <div className={styles.selectionDot} />}
        <BlockStack gap="200">
            <div className={styles.tileIconWrapper}>
                <Icon source={icon} tone={selected ? "success" : "base"} />
            </div>
            <BlockStack gap="0">
                <Text as="span" variant="bodyMd" fontWeight="bold">
                    {title}
                </Text>
                <Text as="span" variant="bodySm" tone="subdued">
                    {description}
                </Text>
            </BlockStack>
        </BlockStack>
    </div>
);

type Props_Discount = {
    dispatchBundleConfig: any;
    discount_type: Field<DiscountTypes>;
    discount_value: Field<number>;
    hide_prices_product_card?: Field<boolean>;
    order_discounts?: Field<boolean>;
    product_discounts?: Field<boolean>;
    shipping_discounts?: Field<boolean>;
    isMobile: boolean;
    bundleType: string;
    products?: any;
    discount_options?: Field<BBTierOptions[]>;
    tier_discount_basis?: Field<any>;
    show_progress_bar?: any; // For BB
    user_id?: null | number;
    enable_free_shipping?: Field<boolean>;
    apply_discount_on_individual_products?: Field<boolean>;
    individual_product_discounts?: Field<{
        [productId: string]: {
            discount_type: DiscountTypes;
            discount_value: number;
        };
    }>;
    discount_label_template?: Field<string>;
    b2b_discount_value?: Field<number>;
    b2b_discounts_enabled?: Field<boolean>;
    discount_label_template_helptext?: string;
    same_addon_for_all_variants?: Field<boolean>;
    fixed_boxes?: Field<FixedBoxConfig[]>;
    fixed_boxes_upsell_message?: Field<string>;
    fixed_boxes_success_message?: Field<string>;
    hide_bundle_header?: Field<boolean>;
};

export const DiscountCombinations = ({
    dispatchBundleConfig,
    order_discounts,
    product_discounts,
    shipping_discounts,
    alone = false,
    bundleType,
    show_progress_bar,
    discount_options,
}: {
    dispatchBundleConfig: any;
    order_discounts?: Field<boolean> | null;
    product_discounts?: Field<boolean> | null;
    shipping_discounts?: Field<boolean> | null;
    alone?: boolean;
    bundleType: any;
    show_progress_bar?: Field<boolean>;
    discount_options?: any;
}) => {
    const { trackFormSections } = useBundleMixpanelHook();
    const [expandDiscountCombination, setExpandDiscountCombination] = useState(true);
    const [expandAdvanceDiscountCombination, setexpandAdvanceDiscountCombination] = useState(false);

    const handleUpdateDiscountOptions = (updated: MixMatchOption[]) => {
        discount_options.onChange(updated);
        dispatchBundleConfig({
            payload: updated,
            key: "discount_options",
        });
    };

    const handleDiscountInputChange = (
        index: number,
        field: keyof MixMatchOption,
        value: string | number | boolean | null,
    ) => {
        const updatedDiscountOptions = [...discount_options.value];
        if (field && index >= 0 && updatedDiscountOptions[index] && value != null) {
            // Create a new object to avoid direct state mutation
            const updatedItem = { ...updatedDiscountOptions[index], [field]: value };

            if (field == "showFinalSuccessMessage" && value && !updatedItem?.finalSuccessMessage?.length) {
                updatedItem.finalSuccessMessage = "🎉 Congratulations! You've unlocked the best offer on this bundle.";
            }
            updatedDiscountOptions[index] = updatedItem;
        }
        handleUpdateDiscountOptions(updatedDiscountOptions);
    };

    // if (bundleType === BundleTypes.THANKYOUPAGE) return null;
    return (
        <div style={{ ...(alone && { marginTop: "15px" }) }}>
            {bundleType === BundleTypes.BXGY && (
                <div
                    className={styles.customLabelWrapper}
                    style={{ marginBottom: "10px", display: "flex", justifyContent: "space-between" }}
                >
                    <Label id="discount_combinations">Discount combinations</Label>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: expandDiscountCombination ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                        // onClick={() => setExpandDiscountCombination((prev) => !prev)}
                        onClick={() =>
                            setExpandDiscountCombination((ps) => {
                                trackFormSections({
                                    cta: "Form Section",
                                    ctaType: `Discount ${ps ? "Collapsed" : "Expanded"}`,
                                    bundleType: bundleType,
                                });
                                return !ps;
                            })
                        }
                    >
                        <MdOutlineArrowBackIosNew size={14} />
                    </div>
                </div>
            )}
            <Collapsible
                open={bundleType !== BundleTypes.BXGY ? true : expandDiscountCombination}
                id="basic-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <>
                    {show_progress_bar &&
                        [BundleTypes.MIX_MATCH, BundleTypes.MIX_MATCH_SECTIONAL].includes(bundleType) && (
                            <Card>
                                <BlockStack gap="400">
                                    <Text as="h2" variant="headingMd">
                                        Discount display & success message
                                    </Text>

                                    <Divider />

                                    <BlockStack gap="200">
                                        <Text as="p" variant="bodyMd" fontWeight="medium">
                                            How should discounts be displayed to customers?
                                        </Text>

                                        <ChoiceList
                                            title="Discount display type"
                                            titleHidden
                                            choices={[
                                                { label: "Show progress bar", value: "progress" },
                                                { label: "Show discount boxes", value: "boxes" },
                                            ]}
                                            selected={[show_progress_bar.value ? "progress" : "boxes"]}
                                            onChange={(val) => {
                                                const isProgress = val[0] === "progress";
                                                dispatchBundleConfig({
                                                    payload: isProgress,
                                                    key: "show_progress_bar",
                                                });
                                                show_progress_bar.onChange(isProgress);
                                            }}
                                        />
                                    </BlockStack>

                                    <Divider />

                                    <Checkbox
                                        label="Show success message after all tiers are completed"
                                        checked={discount_options.value?.[0]?.showFinalSuccessMessage}
                                        value={""}
                                        onChange={(val) => handleDiscountInputChange(0, "showFinalSuccessMessage", val)}
                                        disabled={show_progress_bar?.value == false}
                                    />
                                    {show_progress_bar?.value ? (
                                        <>
                                            {discount_options.value?.[0]?.showFinalSuccessMessage && (
                                                <BlockStack gap="200">
                                                    <Text as="h3" variant="bodySm">
                                                        Success message
                                                    </Text>

                                                    <TextField
                                                        label=""
                                                        placeholder="🎉 Congratulations! You've unlocked the best offer on this bundle."
                                                        autoComplete="off"
                                                        value={discount_options.value?.[0]?.finalSuccessMessage}
                                                        onChange={(val) =>
                                                            handleDiscountInputChange(0, "finalSuccessMessage", val)
                                                        }
                                                        maxLength={300}
                                                        helpText="This message appears once the customer completes the final discount tier."
                                                    />
                                                </BlockStack>
                                            )}
                                        </>
                                    ) : (
                                        <Tooltip content="Enable progress bar to customize the success message">
                                            <div
                                                style={{
                                                    opacity: show_progress_bar?.value === false ? 0.5 : 1,
                                                    pointerEvents: show_progress_bar?.value === false ? "none" : "auto",
                                                }}
                                            >
                                                {discount_options.value?.[0]?.showFinalSuccessMessage && (
                                                    <BlockStack gap="200">
                                                        <Text as="h3" variant="bodySm">
                                                            Success message
                                                        </Text>

                                                        <TextField
                                                            label=""
                                                            placeholder="🎉 Congratulations! You've unlocked the best offer on this bundle."
                                                            autoComplete="off"
                                                            value={discount_options.value?.[0]?.finalSuccessMessage}
                                                            onChange={(val) =>
                                                                handleDiscountInputChange(0, "finalSuccessMessage", val)
                                                            }
                                                            maxLength={300}
                                                            helpText="This message appears once the customer completes the final discount tier."
                                                            disabled={show_progress_bar?.value == false}
                                                        />
                                                    </BlockStack>
                                                )}
                                            </div>
                                        </Tooltip>
                                    )}
                                </BlockStack>
                            </Card>
                        )}

                    <div
                        style={{
                            marginTop: [BundleTypes.MIX_MATCH, BundleTypes.MIX_MATCH_SECTIONAL].includes(bundleType)
                                ? "15px"
                                : undefined,
                        }}
                        className={styles.flexHorizontalContainer}
                        onClick={() => setexpandAdvanceDiscountCombination((prev) => !prev)}
                    >
                        <p className={styles.combineTitle}>Combine this discount with other</p>

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                transform: expandAdvanceDiscountCombination ? "rotate(90deg)" : "rotate(270deg)",
                            }}
                        >
                            <MdOutlineArrowBackIosNew size={10} cursor={"pointer"} />
                        </div>
                    </div>

                    <Collapsible
                        open={expandAdvanceDiscountCombination}
                        transition={{
                            duration: "500ms",
                            timingFunction: "ease-in-out",
                        }}
                        expandOnPrint
                        id="basic-collapsible"
                    >
                        <div className={`${styles.formCol}`}>
                            {order_discounts && (
                                <Checkbox
                                    label={"Order discounts"}
                                    {...order_discounts}
                                    value=""
                                    checked={order_discounts.value}
                                    onChange={(val) => {
                                        dispatchBundleConfig({
                                            payload: val,
                                            key: "order_discounts",
                                        });
                                        order_discounts.onChange(val);
                                    }}
                                />
                            )}
                            {product_discounts && (
                                <Checkbox
                                    label={"Product discounts"}
                                    {...product_discounts}
                                    value=""
                                    checked={product_discounts.value}
                                    onChange={(val) => {
                                        dispatchBundleConfig({
                                            payload: val,
                                            key: "product_discounts",
                                        });
                                        product_discounts.onChange(val);
                                    }}
                                />
                            )}
                            {shipping_discounts && (
                                <Checkbox
                                    label={"Shipping discounts"}
                                    {...shipping_discounts}
                                    value=""
                                    checked={shipping_discounts.value}
                                    onChange={(val) => {
                                        dispatchBundleConfig({
                                            payload: val,
                                            key: "shipping_discounts",
                                        });
                                        shipping_discounts.onChange(val);
                                    }}
                                />
                            )}
                        </div>
                    </Collapsible>
                </>
            </Collapsible>
        </div>
    );
};

const Discount = ({
    dispatchBundleConfig,
    discount_type,
    discount_value,
    order_discounts,
    product_discounts,
    shipping_discounts,
    isMobile,
    bundleType = "",
    products,
    hide_prices_product_card,
    discount_options,
    tier_discount_basis,
    show_progress_bar,
    user_id = null,
    enable_free_shipping,
    apply_discount_on_individual_products,
    individual_product_discounts,
    discount_label_template,
    b2b_discount_value,
    b2b_discounts_enabled,
    fixed_boxes,
    fixed_boxes_upsell_message,
    fixed_boxes_success_message,
    hide_bundle_header,
    discount_label_template_helptext,
    same_addon_for_all_variants,
}: Props_Discount) => {
    const autoHiddenRef = useRef(false);
    const [expandDiscount, setExpandDiscount] = useState(false);
    const [isB2bEnabled, setIsB2bEnabled] = useState(false);
    const appConfigContext = useContext(AppConfigContext);
    const { money_format, currency, shop } = appConfigContext;
    const { trackBundleDiscount, trackFormSections } = useBundleMixpanelHook();
    // console.log("discount_value::", discount_value?.value);

    const updateIndividualDiscount = (productId: string, field: "discount_type" | "discount_value", value: any) => {
        const current = individual_product_discounts?.value || {};
        const updated = {
            ...current,
            [productId]: {
                ...(current[productId] || { discount_type: "percentage", discount_value: 0 }),
                [field]: value,
            },
        };

        dispatchBundleConfig({
            payload: updated,
            key: "individual_product_discounts",
        });
        individual_product_discounts?.onChange(updated);
    };

    //for old Bundle builder ui handling. Tier, standard,
    const [lastStandardType, setLastStandardType] = useState<DiscountTypes>(
        ["tier", DISCOUNT_TYPES.FIXED_BOXES].includes(discount_type.value) ? "percentage" : discount_type.value,
    );
    useEffect(() => {
        if (discount_type.value !== "tier" && discount_type.value !== DISCOUNT_TYPES.FIXED_BOXES) {
            setLastStandardType(discount_type.value);
        }
    }, [discount_type.value]);

    let disableFlat = false;
    // To disable Flat discount type for product add on bundle type
    if (bundleType) {
        disableFlat = products?.value?.length > 1;
    }
    useEffect(() => {
        if (bundleType === "product_add_on") {
            if (disableFlat && discount_type.value === DISCOUNT_TYPES.FLAT) {
                dispatchBundleConfig({
                    payload: DISCOUNT_TYPES.NONE,
                    key: "discount_type",
                });
                discount_type.onChange(DISCOUNT_TYPES.NONE);
            }
        }
    }, [disableFlat]);

    useEffect(() => {
        const currentValue = +discount_value.value || 0;

        // If the discount type is "percentage", cap the value at 100
        if (discount_type.value === "percentage" && currentValue > 100) {
            dispatchBundleConfig({
                payload: 100, // Cap value at 100 for percentage
                key: "discount_value",
            });
            discount_value.onChange(100);
        }
        // } else {
        //     // Dispatch the current value for other discount types
        //     dispatchBundleConfig({
        //         payload: currentValue,
        //         key: "discount_value",
        //     });
        // }

        if (discount_type.value === "none") {
            dispatchBundleConfig({
                payload: 1,
                key: "discount_value",
            });
            discount_value.onChange(1);
        }
    }, [discount_type.value]);

    // useEffect(() => {
    //     // it maximizes the applicable discount on all products in standard
    //     // in bxgy bundle,it  maximizes the discount on discounted_products only
    //     if (discount_type.value == "flat" || discount_type.value == "fixed") {
    //         let p = 0;
    //         for (let i = 0; i < products.value.length; i++) {
    //             const element = products.value[i].variants;
    //             let maxxPrice = 0;
    //             for (let index = 0; index < element.length; index++) {
    //                 const element2 = element[index];
    //                 maxxPrice = Math.max(maxxPrice, element2.price);
    //             }
    //             p += maxxPrice;
    //         }

    //         if (Number(discount_value.value) > p) {
    //             dispatchBundleConfig({
    //                 payload: p,
    //                 key: "discount_value",
    //             });
    //             discount_value.onChange(p);
    //         }
    //     }
    // }, [discount_type.value, discount_value.value]);

    const BundleBuilderFilteredOptions = getDiscountOptionsBB(money_format.split("{{")[0].trim()).filter((option) => {
        // hide 'Fixed bundle price' for old user Id's
        // if (option.value === DISCOUNT_TYPES.FIXED && user_id && user_id > 785) return false;
        return true;
    });

    useEffect(() => {
        if (same_addon_for_all_variants?.value === false && apply_discount_on_individual_products?.value === true) {
            dispatchBundleConfig({
                payload: false,
                key: "apply_discount_on_individual_products",
            });
            apply_discount_on_individual_products?.onChange(false);
        }
    }, [same_addon_for_all_variants?.value, apply_discount_on_individual_products?.value]);

    return (
        <>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandDiscount((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Discount ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <MantleDoc handle={mantleDocHandles[bundleType].setUpDiscounts} title="Discounts" />
                {bundleType !== BundleTypes.BXGY && (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: expandDiscount ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <MdOutlineArrowBackIosNew size={14} />
                    </div>
                )}
            </div>
            <Collapsible
                open={bundleType === BundleTypes.BXGY ? true : expandDiscount}
                id="basic-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <FormLayout>
                    <div
                        className={`${styles.formRow} `}
                        style={{
                            gridTemplateColumns:
                                bundleType === BundleTypes.PRODUCT_ADDONS ||
                                bundleType === BundleTypes.BUNDLE_BUILDER ||
                                bundleType === BundleTypes.BB_PLACEHOLDER
                                    ? "1fr"
                                    : "3fr 2fr",
                            gap: isMobile ? "5px" : "16px",
                            display: isMobile ? "flex" : "",
                            flexDirection: "column",
                        }}
                    >
                        {bundleType === "bxgy" ? (
                            <Select
                                label={"Discount Type"}
                                placeholder={"Select discount type"}
                                options={getDiscountOptionsBXGY(money_format.split("{{")[0].trim())}
                                onBlur={() => discount_type?.runValidation()}
                                onChange={(val) => {
                                    dispatchBundleConfig({
                                        payload: val,
                                        key: "discount_type",
                                    });
                                    discount_type.onChange(val as unknown as DiscountTypes);
                                }}
                                value={discount_type.value}
                            />
                        ) : bundleType === "product_add_on" ? (
                            <>
                                <Text as="p" variant="bodyMd" fontWeight="regular">
                                    Configure how discounts are applied to products in this bundle.
                                </Text>

                                <Select
                                    label={
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "row",
                                                alignItems: "center",
                                            }}
                                        >
                                            Discount Type
                                            <div>
                                                <Tooltip
                                                    width="wide"
                                                    content={
                                                        "Fixed amount discounts can be applied when only one product is offered as an add-on"
                                                    }
                                                >
                                                    <Icon source={InfoIcon} tone="subdued" />
                                                </Tooltip>
                                            </div>
                                        </div>
                                    }
                                    options={getDiscountOptionsAddons(money_format.split("{{")[0].trim(), disableFlat)}
                                    onBlur={() => discount_type.runValidation()}
                                    onChange={(val) => {
                                        dispatchBundleConfig({
                                            payload: val,
                                            key: "discount_type",
                                        });
                                        discount_type.onChange(val as DiscountTypes);

                                        // Syncing individual product discount types
                                        if (
                                            apply_discount_on_individual_products?.value &&
                                            individual_product_discounts?.value
                                        ) {
                                            const updated: any = {};
                                            Object.keys(individual_product_discounts.value).forEach((pid) => {
                                                updated[pid] = {
                                                    ...individual_product_discounts.value[pid],
                                                    discount_type: val,
                                                };
                                            });

                                            dispatchBundleConfig({
                                                payload: updated,
                                                key: "individual_product_discounts",
                                            });
                                            individual_product_discounts.onChange(updated);
                                        }
                                    }}
                                    value={discount_type.value}
                                />
                                {discount_type?.value !== DISCOUNT_TYPES.NONE && (
                                    <>
                                        <Divider borderColor="border" />

                                        <Label id="apply_discount_on_individual_products">Apply discount to</Label>

                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            <RadioButton
                                                label="All selected products"
                                                id="all_products"
                                                name="apply_discount_on_individual_products"
                                                checked={apply_discount_on_individual_products?.value === false}
                                                onChange={(val) => {
                                                    if (!val) return;
                                                    dispatchBundleConfig({
                                                        payload: false,
                                                        key: "apply_discount_on_individual_products",
                                                    });
                                                    apply_discount_on_individual_products?.onChange(false);
                                                }}
                                            />

                                            <RadioButton
                                                label="Individual products (set different values)"
                                                id="individual_products"
                                                name="apply_discount_on_individual_products"
                                                checked={apply_discount_on_individual_products?.value === true}
                                                onChange={(val) => {
                                                    if (!val) return;
                                                    dispatchBundleConfig({
                                                        payload: true,
                                                        key: "apply_discount_on_individual_products",
                                                    });
                                                    apply_discount_on_individual_products?.onChange(true);

                                                    // Initialize discounts for all products if switching to individual
                                                    if (val === true && products?.value) {
                                                        const initialized: any = {};
                                                        products.value.forEach((product: any) => {
                                                            initialized[product.id] = {
                                                                discount_type: discount_type.value,
                                                                discount_value: discount_value.value || 5,
                                                            };
                                                        });
                                                        dispatchBundleConfig({
                                                            payload: initialized,
                                                            key: "individual_product_discounts",
                                                        });
                                                        individual_product_discounts?.onChange(initialized);
                                                    }
                                                }}
                                                disabled={same_addon_for_all_variants?.value === false}
                                            />
                                        </div>

                                        {/* ALL PRODUCTS DISCOUNT */}
                                        {apply_discount_on_individual_products?.value === false && (
                                            <div
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                                                    gap: "12px",
                                                }}
                                            >
                                                <TextField
                                                    type="number"
                                                    label="Discount value"
                                                    placeholder="e.g. 10"
                                                    min={0}
                                                    max={100}
                                                    suffix={
                                                        getNumberFieldSuffix(discount_type.value, money_format).suffix
                                                    }
                                                    value={discount_value.value?.toString() || ""}
                                                    onChange={(val) => {
                                                        const regex = /^(\d+(\.\d{0,2})?)?$/;
                                                        if (regex.test(val)) {
                                                            let num = +val;
                                                            if (num > 100) num = 100;

                                                            dispatchBundleConfig({
                                                                payload: num,
                                                                key: "discount_value",
                                                            });
                                                            discount_value.onChange(num);
                                                        }
                                                    }}
                                                    error={!!discount_value.error}
                                                    autoComplete="off"
                                                />

                                                {discount_label_template && (
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "7px",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "center",
                                                            }}
                                                        >
                                                            <span style={{ fontWeight: 500 }}>Discount label</span>

                                                            <Tooltip
                                                                width="wide"
                                                                content={discount_label_template_helptext}
                                                            >
                                                                <PiBracketsCurlyBold />
                                                            </Tooltip>
                                                        </div>
                                                        <TextField
                                                            label="Discount label"
                                                            labelHidden
                                                            placeholder="Discount label"
                                                            autoComplete="off"
                                                            maxLength={80}
                                                            {...discount_label_template}
                                                            onBlur={() => discount_label_template?.runValidation()}
                                                            onChange={(val) => {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "discount_label_template",
                                                                });
                                                                discount_label_template?.onChange(val);
                                                            }}
                                                            showCharacterCount
                                                        />
                                                    </div>
                                                )}

                                                {discount_value.error && (
                                                    <InlineError message="Required" fieldID="discount_value" />
                                                )}
                                            </div>
                                        )}

                                        {/* Individual Product Discounts */}
                                        {apply_discount_on_individual_products?.value === true && products?.value && (
                                            <>
                                                <div
                                                    style={{
                                                        border: "1px solid #E1E3E5",
                                                        borderRadius: "8px",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    {/* Table Header */}
                                                    <div
                                                        style={{
                                                            display: "grid",
                                                            gridTemplateColumns: "2fr 1fr 1fr",
                                                            gap: "16px",
                                                            padding: "12px 16px",
                                                            backgroundColor: "#F6F6F7",
                                                            borderBottom: "1px solid #E1E3E5",
                                                            fontWeight: "600",
                                                            fontSize: "13px",
                                                        }}
                                                    >
                                                        <div>Product</div>
                                                        <div>Discount value</div>
                                                        <div>Final price</div>
                                                    </div>

                                                    {/* Table Rows */}
                                                    {products.value.map((product: any, index: any) => {
                                                        const productDiscount = individual_product_discounts?.value?.[
                                                            product.id
                                                        ] || {
                                                            discount_type: discount_type.value || "percentage",
                                                            discount_value: discount_value.value || 5,
                                                        };

                                                        // Calculate final price
                                                        const basePrice = product.variants[0]?.price || 0;
                                                        let finalPrice = +basePrice;

                                                        if (discount_type.value === "percentage") {
                                                            finalPrice =
                                                                +basePrice * (1 - productDiscount.discount_value / 100);
                                                        } else if (discount_type.value === "flat") {
                                                            finalPrice = Math.max(
                                                                0,
                                                                +basePrice - productDiscount.discount_value,
                                                            );
                                                        }

                                                        const isRequired =
                                                            !productDiscount.discount_value &&
                                                            productDiscount.discount_value !== 0;

                                                        return (
                                                            <div
                                                                key={product.id}
                                                                style={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: "2fr 1fr 1fr",
                                                                    gap: "16px",
                                                                    padding: "16px",
                                                                    borderBottom:
                                                                        index < products.value.length - 1
                                                                            ? "1px solid #E1E3E5"
                                                                            : "none",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                {/* Product Info */}
                                                                <div
                                                                    style={{
                                                                        fontWeight: "500",
                                                                        marginBottom: "4px",
                                                                    }}
                                                                >
                                                                    {product.title}
                                                                </div>

                                                                {/* Discount Input */}

                                                                <TextField
                                                                    type="number"
                                                                    label=""
                                                                    placeholder="e.g. 10"
                                                                    min={0}
                                                                    max={
                                                                        discount_type.value === "percentage"
                                                                            ? 100
                                                                            : undefined
                                                                    }
                                                                    suffix={
                                                                        isMobile
                                                                            ? ""
                                                                            : getNumberFieldSuffix(
                                                                                  discount_type.value,
                                                                                  money_format,
                                                                              ).suffix
                                                                    }
                                                                    value={
                                                                        productDiscount.discount_value?.toString() || ""
                                                                    }
                                                                    onChange={(val) => {
                                                                        const regex = /^(\d+(\.\d{0,2})?)?$/;
                                                                        if (regex.test(val)) {
                                                                            let numVal = +val;
                                                                            if (
                                                                                discount_type.value === "percentage" &&
                                                                                numVal > 100
                                                                            ) {
                                                                                numVal = 100;
                                                                            }
                                                                            updateIndividualDiscount(
                                                                                product.id,
                                                                                "discount_value",
                                                                                numVal,
                                                                            );
                                                                        }
                                                                    }}
                                                                    error={isRequired}
                                                                    autoComplete="off"
                                                                />

                                                                {/* Final Price */}
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        flexDirection: isMobile ? "column" : "row",
                                                                        gap: "4px",
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            fontWeight: "600",
                                                                            fontSize: "14px",
                                                                        }}
                                                                    >
                                                                        {getPriceLabel(
                                                                            finalPrice,
                                                                            money_format,
                                                                            currency,
                                                                        )}
                                                                    </div>
                                                                    {productDiscount.discount_value > 0 && (
                                                                        <div
                                                                            style={{
                                                                                fontSize: "12px",
                                                                                color: "#6D7175",
                                                                                textDecoration: "line-through",
                                                                            }}
                                                                        >
                                                                            {getPriceLabel(
                                                                                +basePrice,
                                                                                money_format,
                                                                                currency,
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {individual_product_discounts?.error && (
                                                    <InlineError
                                                        message={individual_product_discounts.error}
                                                        fieldID="individual_product_discounts"
                                                    />
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        ) : bundleType === "product_add_on_popup" ? (
                            <Select
                                label={"Discount Type"}
                                placeholder={"Select discount type"}
                                options={getDiscountOptionsAddonsPopup()}
                                onBlur={() => discount_type.runValidation()}
                                onChange={(val) => {
                                    dispatchBundleConfig({
                                        payload: val,
                                        key: "discount_type",
                                    });
                                    discount_type.onChange(val as unknown as DiscountTypes);
                                }}
                                value={discount_type.value}
                            />
                        ) : bundleType === "thank-you-page" ? (
                            <Select
                                label={"Discount Type"}
                                placeholder={"Select discount type"}
                                options={
                                    // @ts-expect-error required
                                    getDiscountOptionsThankyou(money_format.split("{{")[0].trim())
                                }
                                onBlur={() => discount_type.runValidation()}
                                onChange={(val) => {
                                    dispatchBundleConfig({
                                        payload: val,
                                        key: "discount_type",
                                    });
                                    discount_type.onChange(val as unknown as DiscountTypes);
                                }}
                                value={discount_type.value}
                            />
                        ) : bundleType === BundleTypes.BB_PLACEHOLDER ? (
                            <PlaceholderDiscount
                                dispatchBundleConfig={dispatchBundleConfig}
                                discount_type={discount_type}
                                lastStandardType={lastStandardType}
                                money_format={money_format}
                                discount_value={discount_value}
                                bundleType={bundleType}
                                trackBundleDiscount={trackBundleDiscount}
                                hide_prices_product_card={hide_prices_product_card}
                                discount_options={discount_options}
                                tier_discount_basis={tier_discount_basis}
                                show_progress_bar={show_progress_bar}
                                products={products}
                                setLastStandardType={setLastStandardType}
                            />
                        ) : bundleType === BundleTypes.BUNDLE_BUILDER ? (
                            <>
                                {/* <Select
                                    label={"Discount Type"}
                                    placeholder={"Select discount type"}
                                    options={getDiscountOptionsBB(money_format.split("{{")[0].trim())}
                                    onBlur={() => discount_type.runValidation()}
                                    onChange={(val) => {
                                        dispatchBundleConfig({
                                            payload: val,
                                            key: "discount_type",
                                        });
                                        discount_type.onChange(val as unknown as DiscountTypes);
                                    }}
                                    value={discount_type.value}
                                /> */}

                                {[
                                    "quickstart-f02489ba",
                                    "newteststore-290133",
                                    "trustbadgetest",
                                    "javv39-ze",
                                    "sdhieur",
                                ].includes(shop) && (
                                    <>
                                        <ButtonGroup variant="segmented">
                                            <Button
                                                pressed={isB2bEnabled === false}
                                                onClick={() => {
                                                    setIsB2bEnabled(false);
                                                }}
                                            >
                                                B2C
                                            </Button>
                                            <Button
                                                pressed={isB2bEnabled === true}
                                                onClick={() => {
                                                    setIsB2bEnabled(true);
                                                }}
                                            >
                                                B2B
                                            </Button>
                                        </ButtonGroup>
                                        <Divider />
                                    </>
                                )}

                                {!isB2bEnabled && (
                                    <BlockStack gap="300">
                                        <Text as="h3" variant="headingSm" fontWeight="bold">
                                            1. Choose Pricing Strategy
                                        </Text>
                                        <div className={styles.tilesGrid}>
                                            <PricingStrategyTile
                                                title="Tiered"
                                                description="Buy more, save more"
                                                icon={MdOutlineTrendingUp}
                                                selected={discount_type?.value == "tier"}
                                                onClick={() => {
                                                    dispatchBundleConfig({
                                                        payload: "tier",
                                                        key: "discount_type",
                                                    });
                                                    discount_type.onChange("tier" as unknown as DiscountTypes);
                                                    if (autoHiddenRef.current && hide_bundle_header?.value) {
                                                        hide_bundle_header?.onChange(false);
                                                        dispatchBundleConfig({
                                                            payload: false,
                                                            key: "hide_bundle_header",
                                                        });
                                                        autoHiddenRef.current = false;
                                                    }
                                                }}
                                            />

                                            <PricingStrategyTile
                                                title="Fixed Boxes"
                                                description="Set box sizes"
                                                icon={PackageIcon}
                                                selected={discount_type.value === DISCOUNT_TYPES.FIXED_BOXES}
                                                onClick={() => {
                                                    dispatchBundleConfig({
                                                        payload: DISCOUNT_TYPES.FIXED_BOXES,
                                                        key: "discount_type",
                                                    });
                                                    discount_type.onChange(DISCOUNT_TYPES.FIXED_BOXES);
                                                    if ((fixed_boxes?.value || []).length === 0) {
                                                        const initialBox = {
                                                            itemsCount: 2,
                                                            fixedPrice: 50,
                                                            boxName: "Box of 2",
                                                        };
                                                        fixed_boxes?.onChange([initialBox]);
                                                        dispatchBundleConfig({
                                                            key: "fixed_boxes",
                                                            payload: [initialBox],
                                                        });
                                                    }
                                                    if (!hide_bundle_header?.value) {
                                                        hide_bundle_header?.onChange(true);
                                                        dispatchBundleConfig({
                                                            payload: true,
                                                            key: "hide_bundle_header",
                                                        });
                                                        autoHiddenRef.current = true;
                                                    }
                                                }}
                                            />

                                            <PricingStrategyTile
                                                title="Single"
                                                description="Build & save"
                                                icon={DiscountIcon}
                                                selected={
                                                    discount_type.value !== "tier" &&
                                                    discount_type.value !== DISCOUNT_TYPES.FIXED_BOXES &&
                                                    discount_type.value !== DISCOUNT_TYPES.NONE
                                                }
                                                onClick={() => {
                                                    const payload =
                                                        lastStandardType === DISCOUNT_TYPES.NONE
                                                            ? DISCOUNT_TYPES.PERCENTAGE
                                                            : lastStandardType;
                                                    dispatchBundleConfig({
                                                        payload,
                                                        key: "discount_type",
                                                    });
                                                    discount_type.onChange(payload);
                                                    if (autoHiddenRef.current && hide_bundle_header?.value) {
                                                        hide_bundle_header?.onChange(false);
                                                        dispatchBundleConfig({
                                                            payload: false,
                                                            key: "hide_bundle_header",
                                                        });
                                                        autoHiddenRef.current = false;
                                                    }
                                                }}
                                            />

                                            <PricingStrategyTile
                                                title="No Discount"
                                                description="Standard pricing"
                                                icon={MdOutlineBlock}
                                                selected={discount_type.value === DISCOUNT_TYPES.NONE}
                                                onClick={() => {
                                                    dispatchBundleConfig({
                                                        payload: DISCOUNT_TYPES.NONE,
                                                        key: "discount_type",
                                                    });
                                                    discount_type.onChange(DISCOUNT_TYPES.NONE);
                                                    if (autoHiddenRef.current && hide_bundle_header?.value) {
                                                        hide_bundle_header?.onChange(false);
                                                        dispatchBundleConfig({
                                                            payload: false,
                                                            key: "hide_bundle_header",
                                                        });
                                                        autoHiddenRef.current = false;
                                                    }
                                                }}
                                            />
                                        </div>
                                    </BlockStack>
                                )}
                            </>
                        ) : (
                            <Select
                                label={"Discount Type"}
                                placeholder={"Select discount type"}
                                options={getDiscountOptions(money_format.split("{{")[0].trim())}
                                onBlur={() => discount_type.runValidation()}
                                onChange={(val) => {
                                    dispatchBundleConfig({
                                        payload: val,
                                        key: "discount_type",
                                    });
                                    trackBundleDiscount({
                                        cta: "Discount",
                                        type: val,
                                        value: discount_value.value?.toString(),
                                        bundleType: bundleType,
                                    });
                                    discount_type.onChange(val as unknown as DiscountTypes);
                                    discount_type.onChange(val as unknown as DiscountTypes);
                                    if (hide_prices_product_card) {
                                        dispatchBundleConfig({
                                            payload: false,
                                            key: "hide_prices_product_card",
                                        });
                                        hide_prices_product_card.onChange(false);
                                    }
                                }}
                                value={discount_type.value}
                            />
                        )}

                        {!(
                            (bundleType == BundleTypes.BUNDLE_BUILDER && discount_type.value != DISCOUNT_TYPES.TIER) ||
                            bundleType == BundleTypes.PRODUCT_ADDONS ||
                            bundleType == BundleTypes.BB_PLACEHOLDER
                        ) &&
                            getNumberFieldSuffix(discount_type.value, money_format).show && (
                                <TextField
                                    type={"number"}
                                    label={"Value"}
                                    placeholder="value"
                                    min={0}
                                    suffix={getNumberFieldSuffix(discount_type.value, money_format).suffix}
                                    {...discount_value}
                                    value={discount_value.value.toString()}
                                    autoComplete="off"
                                    onBlur={() => {
                                        discount_value.runValidation();
                                        trackBundleDiscount({
                                            cta: "Discount",
                                            value: discount_value.value?.toString(),
                                            type: discount_type.value,
                                            bundleType: bundleType,
                                        });
                                    }}
                                    onChange={(val) => {
                                        const regex = /^(\d+(\.\d{0,2})?)?$/;
                                        if (regex.test(val)) {
                                            if (discount_type.value === "percentage" && +val > 100) {
                                                val = "100";
                                            }

                                            dispatchBundleConfig({
                                                payload: +val,
                                                key: "discount_value",
                                            });

                                            discount_value.onChange(val as unknown as number);
                                        }
                                    }}
                                />
                            )}
                    </div>
                    {bundleType == BundleTypes.BUNDLE_BUILDER &&
                        discount_type.value == DISCOUNT_TYPES.TIER &&
                        !isB2bEnabled && (
                            <div
                                className={`${styles.formRow} `}
                                style={{
                                    gridTemplateColumns: "1fr",
                                }}
                            >
                                <BBTierDiscounts
                                    dispatchBundleConfig={dispatchBundleConfig}
                                    discount_options={discount_options as Field<BBTierOptions[]>}
                                    products={products}
                                    tier_discount_basis={tier_discount_basis as any}
                                    show_progress_bar={show_progress_bar}
                                    bundleType={bundleType}
                                />
                            </div>
                        )}

                    {bundleType == BundleTypes.BUNDLE_BUILDER &&
                        discount_type.value == DISCOUNT_TYPES.FIXED_BOXES &&
                        !isB2bEnabled && (
                            <div
                                className={`${styles.formRow} `}
                                style={{
                                    gridTemplateColumns: "1fr",
                                    minWidth: 0,
                                }}
                            >
                                <FixedBoxesConfig
                                    dispatchBundleConfig={dispatchBundleConfig}
                                    fixed_boxes={fixed_boxes as Field<FixedBoxConfig[]>}
                                    fixed_boxes_upsell_message={fixed_boxes_upsell_message as Field<string>}
                                    fixed_boxes_success_message={fixed_boxes_success_message as Field<string>}
                                />
                            </div>
                        )}

                    {bundleType == BundleTypes.BUNDLE_BUILDER &&
                        discount_type.value != DISCOUNT_TYPES.TIER &&
                        discount_type.value != DISCOUNT_TYPES.NONE &&
                        discount_type.value != DISCOUNT_TYPES.FIXED_BOXES &&
                        !isB2bEnabled && (
                            <BlockStack gap="200">
                                <Text as="h3" variant="headingSm" fontWeight="bold">
                                    3. Configure Offer
                                </Text>

                                <Card background="bg-surface-secondary">
                                    <div className={`${styles.formRow} `}>
                                        <Select
                                            label={"Discount Type"}
                                            placeholder={"Select discount type"}
                                            options={BundleBuilderFilteredOptions.filter(
                                                (option) => option.value !== DISCOUNT_TYPES.NONE,
                                            )}
                                            onBlur={() => discount_type.runValidation()}
                                            onChange={(val) => {
                                                dispatchBundleConfig({
                                                    payload: val,
                                                    key: "discount_type",
                                                });
                                                discount_type.onChange(val as unknown as DiscountTypes);
                                                setLastStandardType(val as DiscountTypes);
                                            }}
                                            value={discount_type.value}
                                        />

                                        <TextField
                                            type={"number"}
                                            label={"Value"}
                                            placeholder="value"
                                            min={0}
                                            suffix={getNumberFieldSuffix(discount_type.value, money_format).suffix}
                                            {...discount_value}
                                            value={discount_value.value.toString()}
                                            autoComplete="off"
                                            onBlur={() => {
                                                discount_value.runValidation();
                                                trackBundleDiscount({
                                                    cta: "Discount",
                                                    value: discount_value.value?.toString(),
                                                    type: discount_type.value,
                                                    bundleType: bundleType,
                                                });
                                            }}
                                            onChange={(val) => {
                                                const regex = /^(\d+(\.\d{0,2})?)?$/;
                                                if (regex.test(val)) {
                                                    if (discount_type.value === "percentage" && +val > 100) {
                                                        val = "100";
                                                    }

                                                    dispatchBundleConfig({
                                                        payload: +val,
                                                        key: "discount_value",
                                                    });

                                                    discount_value.onChange(val as unknown as number);
                                                }
                                            }}
                                        />
                                    </div>
                                </Card>
                            </BlockStack>
                        )}

                    {isB2bEnabled && (
                        <B2bDiscountOptions
                            dispatchBundleConfig={dispatchBundleConfig}
                            b2b_discount_value={b2b_discount_value}
                            b2b_discounts_enabled={b2b_discounts_enabled}
                        />
                    )}

                    {bundleType == BundleTypes.BUNDLE_BUILDER &&
                        discount_type.value == DISCOUNT_TYPES.FIXED &&
                        hide_prices_product_card && (
                            <div
                                className={`${styles.formRow} `}
                                style={{
                                    gridTemplateColumns: "1fr",
                                }}
                            >
                                <Checkbox
                                    label={"Hide Prices on Product Card"}
                                    {...hide_prices_product_card}
                                    value=""
                                    checked={!!hide_prices_product_card?.value}
                                    onChange={(val) => {
                                        dispatchBundleConfig({
                                            payload: val,
                                            key: "hide_prices_product_card",
                                        });
                                        hide_prices_product_card.onChange(val);
                                    }}
                                />
                            </div>
                        )}

                    {discount_label_template && bundleType !== BundleTypes.PRODUCT_ADDONS && (
                        <TextField
                            label="Discount label"
                            placeholder="Discount label"
                            autoComplete="off"
                            maxLength={80}
                            {...discount_label_template}
                            onBlur={() => discount_label_template?.runValidation()}
                            onChange={(val) => {
                                dispatchBundleConfig({
                                    payload: val,
                                    key: "discount_label_template",
                                });
                                discount_label_template?.onChange(val);
                            }}
                            showCharacterCount
                            helpText={discount_label_template_helptext}
                        />
                    )}

                    <DiscountCombinations
                        dispatchBundleConfig={dispatchBundleConfig}
                        order_discounts={order_discounts}
                        product_discounts={product_discounts}
                        shipping_discounts={shipping_discounts}
                        bundleType={bundleType}
                    />

                    {bundleType === BundleTypes.THANKYOUPAGE && enable_free_shipping && (
                        <Checkbox
                            label="Enable Free Shipping for  Offer"
                            checked={enable_free_shipping.value}
                            onChange={(checked) => {
                                dispatchBundleConfig({
                                    payload: checked,
                                    key: "enable_free_shipping",
                                });
                                enable_free_shipping.onChange(checked);
                            }}
                        />
                    )}
                </FormLayout>
            </Collapsible>
        </>
    );
};

export default Discount;

const B2bDiscountOptions = ({
    b2b_discount_value,
    b2b_discounts_enabled,
    dispatchBundleConfig,
}: {
    b2b_discount_value?: Field<number>;
    b2b_discounts_enabled?: Field<boolean>;
    dispatchBundleConfig: any;
}) => {
    if (!b2b_discount_value || !b2b_discounts_enabled) return null;

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
            }}
        >
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <ToggleSwitch
                    checked={b2b_discounts_enabled.value}
                    setToggle={(e: any) => {
                        e?.preventDefault();
                        e.stopPropagation();
                        const newVal = !b2b_discounts_enabled.value;
                        b2b_discounts_enabled.onChange(newVal);
                        dispatchBundleConfig({
                            payload: newVal,
                            key: "b2b_discounts_enabled",
                        });
                    }}
                    disabled={false}
                    zoom={0.8}
                />
                <Label id={"hello"}>Turn on B2B discounts</Label>
            </div>

            <div
                style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                }}
            >
                <Text as="h5" alignment="center">
                    Percentage Discount
                </Text>

                <TextField
                    type={"number"}
                    label={"Value"}
                    labelHidden
                    placeholder="value"
                    min={0}
                    suffix={"%"}
                    {...b2b_discount_value}
                    value={b2b_discount_value.value.toString()}
                    autoComplete="off"
                    onBlur={() => {
                        b2b_discount_value.runValidation();
                    }}
                    onChange={(val) => {
                        const regex = /^(\d+(\.\d{0,2})?)?$/;
                        if (regex.test(val)) {
                            if (+val > 100) {
                                val = "100";
                            }
                            dispatchBundleConfig({
                                payload: +val,
                                key: "b2b_discount_value",
                            });
                            b2b_discount_value.onChange(val as unknown as number);
                        }
                    }}
                    disabled={b2b_discounts_enabled.value == false}
                />
            </div>
        </div>
    );
};
